import { useEffect, useMemo } from "react";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { api } from "@/lib/backend";
import { extractionWrapper } from "@/lib/backend/utils/extract";
import { type DayState, buildDayStates } from "@/lib/discover-day-state";
import { type DayKey, type DiscoverWindow } from "@/lib/discover-window";
import { useMe } from "@/lib/hooks/use-me";
import { logger } from "@/logger";

/**
 * How many of your own shifts and applications the calendar reads at once.
 *
 * Deliberately _not_ the paginated `useMyShifts`/`useMyApplications` the agenda uses. Those take
 * ten at a time and load the rest as you scroll, which is right for a list and wrong for a
 * calendar: a grid that marks only your first ten shifts is worse than one that marks none, because
 * it looks complete. So this asks once, over the window the calendar can actually reach, in a
 * single page big enough that nobody realistically falls off the end of it.
 */
const ENGAGEMENT_TAKE = 500;

/**
 * Your own footprint on the days Ontdek is showing.
 *
 * Three sources, because that is how many places "what did I do about this day" lives: shifts you
 * are planned into and applications you sent. They are merged into one state per day by
 * {@link buildDayStates}, which owns the priority rules.
 */
export function useDiscoverDayStates({
  window,
  availableDays,
  enabled = true,
}: {
  window: DiscoverWindow;
  availableDays: ReadonlySet<DayKey>;
  enabled?: boolean;
}) {
  const { me } = useMe();
  const employeeId = me?.employee?.id;

  const from = window.from.toISOString();
  const to = window.to.toISOString();

  const shifts = useQuery({
    queryKey: ["shifts", "discover-day-states", employeeId, from, to] as const,
    queryFn: async () => {
      const query = {
        pagination: { take: ENGAGEMENT_TAKE, page: 1 },
        sort: { field: "startAt", order: "asc" },
        filter: [
          {
            AND: [
              { employeeId: { value: employeeId, operator: "equals" } },
              { startAt: { value: from, operator: "gte" } },
              { startAt: { value: to, operator: "lte" } },
            ],
          },
        ],
      } satisfies Parameters<typeof api.shift.getAll>[0];

      try {
        return await extractionWrapper(
          api.shift.getAll({
            ...query,
            // The only thing that tells a shift you asked to be let off from one you did not.
            aggregate: { cancellationRequest: { compute: true } },
          }),
        );
      } catch (error) {
        // Zonder deze vangst was een stille storing hier niet te onderscheiden van "je hebt geen
        // diensten": de kalender liet gewoon niets zien.
        //
        // Dit is de enige `/shift`-bevraging in de app met een `aggregate`, en de enige waarvan
        // gemeld is dat er niets terugkomt -- terwijl dezelfde diensten op Diensten wél stippen
        // krijgen, uit dezelfde `buildDayStates`. Eén nuance (afmeldverzoek versus ingepland) mag
        // niet het hele signaal kosten, dus vragen we het nog een keer zonder. Blijft de eerste
        // poging het doen, dan komt dit nooit langs.
        logger.error(
          { error, from, to },
          "Kalenderstippen: diensten opvragen mislukte, opnieuw zonder aggregate",
        );

        return await extractionWrapper(api.shift.getAll(query));
      }
    },
    enabled: enabled && !!employeeId,
    // Paging the calendar a month on widens the window, and a fresh request must not blank the
    // marks that are already right — the dots would strobe on every arrow tap.
    placeholderData: keepPreviousData,
  });

  const applications = useQuery({
    queryKey: ["applications", "discover-day-states", employeeId, from] as const,
    queryFn: () =>
      extractionWrapper(
        api.application.getAll({
          pagination: { take: ENGAGEMENT_TAKE, page: 1 },
          sort: { field: "availabilitySlot.startAt", order: "asc" },
          // No upper bound: `ApplicationFilterKeys` is a flat object, so one key cannot carry both
          // a `gte` and an `lte`. Asking for everything from today on costs nothing here — your own
          // applications are few — and it means paging the calendar forward finds its marks already
          // loaded instead of waiting on a second request.
          filter: {
            "employee.id": { value: employeeId, operator: "equals" },
            "availabilitySlot.startAt": { value: from, operator: "gte" },
          },
          include: { availabilitySlot: { select: true } },
        }),
      ),
    enabled: enabled && !!employeeId,
    placeholderData: keepPreviousData,
  });

  const states = useMemo(
    () =>
      buildDayStates({
        shifts: shifts.data?.items ?? [],
        applications: applications.data?.items ?? [],
        availableDays,
      }),
    [shifts.data?.items, applications.data?.items, availableDays],
  );

  /**
   * Eén regel per antwoord, zodat een missende stip te herleiden is.
   *
   * Er was een melding dat een dienst over negen dagen geen stip kreeg, en van hieruit is niet te
   * zien waar het misgaat: komt de dienst niet terug uit de bevraging (venster, filter, rechten),
   * of komt hij wel terug maar landt hij op een andere dag (tijdzone)? Dat verschil bepaalt aan
   * welke kant je moet zoeken, en zonder dit blijft het gissen.
   *
   * `states.size` en niet de hele map: dit is een spoor, geen kopie van het scherm.
   */
  useEffect(() => {
    if (shifts.isError) {
      logger.error({ error: shifts.error, from, to }, "Kalenderstippen: geen diensten op te halen");
      return;
    }

    if (shifts.data === undefined) {
      return;
    }

    logger.info(
      {
        from,
        to,
        employeeId,
        shifts: shifts.data.items?.length ?? 0,
        applications: applications.data?.items?.length ?? 0,
        markedDays: states.size,
        firstShiftStartAt: shifts.data.items?.[0]?.startAt ?? null,
      },
      "Kalenderstippen: wat er terugkwam",
    );
  }, [shifts.data, applications.data, states, from, to, employeeId]);

  const refetch = async () => {
    await Promise.all([shifts.refetch(), applications.refetch()]);
  };

  return { states, refetch };
}

export type { DayState };
